import { useState } from 'react'
import { publicApi } from '../services/api'
import type { AmbassadorFormData, ReferralSource } from '../types'
import LoadingSpinner from '../components/common/LoadingSpinner'
import ReferralSourceField from '../components/common/ReferralSourceField'
import { formatPhoneNumber, lowercaseEmail, isValidEmail, isValidPhone } from '../utils/formatters'

const AMBASSADOR_PRICE_CENTS = 2500 // $25

const AFFILIATIONS = [
  'Parent/Guardian',
  'Educator',
  'Student 14-17',
  'Community Member',
  'Business Owner',
]

const TSHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL']

const HELP_AREAS = ['Events', 'Social Media', 'Business Outreach']

const formatCurrency = (cents: number): string => {
  return `$${(cents / 100).toFixed(2)}`
}

export default function AmbassadorForm() {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Form state
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [city, setCity] = useState('')
  const [zip, setZip] = useState('')
  const [preferredContactMethod, setPreferredContactMethod] = useState<'email' | 'phone' | 'both' | ''>('')
  const [affiliation, setAffiliation] = useState('')
  const [tshirtSize, setTshirtSize] = useState('')
  const [helpAreas, setHelpAreas] = useState<string[]>([])
  const [statementOfInterest, setStatementOfInterest] = useState('')
  const [fundraisingExpertise, setFundraisingExpertise] = useState('')
  const [agreementAccepted, setAgreementAccepted] = useState(false)
  const [referralSource, setReferralSource] = useState<ReferralSource | ''>('')
  const [referralSourceOther, setReferralSourceOther] = useState('')

  const handleHelpAreaChange = (area: string) => {
    setHelpAreas(prev =>
      prev.includes(area) ? prev.filter(a => a !== area) : [...prev, area]
    )
  }

  const validateForm = (): string | null => {
    if (!firstName.trim()) return 'First name is required'
    if (!lastName.trim()) return 'Last name is required'
    if (!email.trim()) return 'Email is required'
    if (!isValidEmail(email)) return 'Please enter a valid email address'
    if (!phone.trim()) return 'Mobile phone is required'
    if (!isValidPhone(phone)) return 'Please enter a valid 10-digit phone number'
    if (!city.trim()) return 'City is required'
    if (!zip.trim()) return 'Zip code is required'
    if (!/^\d{5}$/.test(zip.replace(/\D/g, ''))) return 'Please enter a valid 5-digit zip code'
    if (!preferredContactMethod) return 'Please select your preferred contact method'
    if (!affiliation) return 'Please select your affiliation'
    if (!tshirtSize) return 'Please select your t-shirt size'
    if (!agreementAccepted) return 'You must accept the Ambassador Agreement to continue'
    if (!referralSource) return 'Please select how you heard about us'
    if (referralSource === 'Other' && !referralSourceOther.trim()) return 'Please specify how you heard about us'
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    setSubmitting(true)

    try {
      const formData: AmbassadorFormData = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: lowercaseEmail(email),
        phone: phone.trim(),
        city: city.trim(),
        zip: zip.replace(/\D/g, ''),
        preferredContactMethod: preferredContactMethod as 'email' | 'phone' | 'both',
        affiliation,
        tshirtSize,
        helpAreas,
        statementOfInterest: statementOfInterest.trim(),
        fundraisingExpertise: fundraisingExpertise.trim(),
        agreementAccepted,
        referralSource: referralSource as ReferralSource,
        referralSourceOther: referralSource === 'Other' ? referralSourceOther.trim() : undefined,
      }

      // Create ambassador submission
      const { ambassadorId } = await publicApi.submitAmbassador(formData)

      // Create checkout session
      const { url } = await publicApi.createAmbassadorCheckout(ambassadorId)

      // Redirect to Stripe
      window.location.href = url
    } catch (err) {
      console.error('Error submitting ambassador form:', err)
      setError(err instanceof Error ? err.message : 'Failed to submit. Please try again.')
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="/images/logo.png"
            alt="School Lunch Angels"
            className="max-w-xs w-full mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-sla-blue mb-2">
            Become an Ambassador
          </h1>
          <p className="text-gray-600">
            Join our mission to eliminate school lunch debt
          </p>
          <div className="mt-4 inline-block text-2xl font-bold text-sla-blue border-b-2 border-sla-blue pb-1">
            Registration Fee: {formatCurrency(AMBASSADOR_PRICE_CENTS)} / year
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Annual membership — renews each year
          </p>
        </div>

        {/* T-shirt Image */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <img
              src="/images/school-lunch-angels-ambassador-tshirt.jpg"
              alt="Ambassador T-Shirt"
              className="w-48 h-auto rounded-lg shadow"
            />
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Ambassador T-Shirt Included
              </h3>
              <p className="text-gray-600 text-sm">
                As a School Lunch Angels Ambassador, you'll receive an official Ambassador t-shirt
                with your first year's registration to wear proudly as you help spread our mission in the community.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
              {error}
            </div>
          )}

          {/* Contact Information */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b">
              Contact Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sla-blue focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sla-blue focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(lowercaseEmail(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sla-blue focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                  placeholder="(555) 123-4567"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sla-blue focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sla-blue focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-1">
                  Zip Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="zip"
                  value={zip}
                  onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  placeholder="12345"
                  maxLength={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sla-blue focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* Preferred Contact Method */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Contact Method <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-4">
                {(['email', 'phone', 'both'] as const).map((method) => (
                  <label key={method} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="contactMethod"
                      value={method}
                      checked={preferredContactMethod === method}
                      onChange={() => setPreferredContactMethod(method)}
                      className="w-4 h-4 text-sla-blue focus:ring-sla-blue"
                    />
                    <span className="text-gray-700 capitalize">
                      {method === 'both' ? 'Email and Phone' : method === 'email' ? 'Email' : 'Phone'}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Ambassador Details */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b">
              Ambassador Details
            </h2>

            {/* Affiliation */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Affiliation <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {AFFILIATIONS.map((aff) => (
                  <label key={aff} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="affiliation"
                      value={aff}
                      checked={affiliation === aff}
                      onChange={() => setAffiliation(aff)}
                      className="w-4 h-4 text-sla-blue focus:ring-sla-blue"
                    />
                    <span className="text-gray-700">{aff}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* T-Shirt Size */}
            <div className="mb-4">
              <label htmlFor="tshirtSize" className="block text-sm font-medium text-gray-700 mb-1">
                T-Shirt Size <span className="text-red-500">*</span>
              </label>
              <select
                id="tshirtSize"
                value={tshirtSize}
                onChange={(e) => setTshirtSize(e.target.value)}
                className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sla-blue focus:border-transparent"
                required
              >
                <option value="">Select size...</option>
                {TSHIRT_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size === 'XS' ? 'Extra Small' : size === 'S' ? 'Small' : size === 'M' ? 'Medium' : size === 'L' ? 'Large' : size === 'XL' ? 'XL' : 'XXL'}
                  </option>
                ))}
              </select>
            </div>

            {/* Areas of Interest */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Areas You Are Interested in Helping <span className="text-gray-400">(optional)</span>
              </label>
              <div className="flex flex-wrap gap-4">
                {HELP_AREAS.map((area) => (
                  <label key={area} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={helpAreas.includes(area)}
                      onChange={() => handleHelpAreaChange(area)}
                      className="w-4 h-4 text-sla-blue rounded focus:ring-sla-blue"
                    />
                    <span className="text-gray-700">{area}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Statement of Interest */}
            <div className="mb-4">
              <label htmlFor="statementOfInterest" className="block text-sm font-medium text-gray-700 mb-1">
                Statement of Interest <span className="text-gray-400">(optional)</span>
              </label>
              <p className="text-sm text-gray-500 mb-2">
                Why are you interested in becoming a School Lunch Angel?
              </p>
              <textarea
                id="statementOfInterest"
                value={statementOfInterest}
                onChange={(e) => setStatementOfInterest(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sla-blue focus:border-transparent"
              />
            </div>

            {/* Fundraising Expertise */}
            <div>
              <label htmlFor="fundraisingExpertise" className="block text-sm font-medium text-gray-700 mb-1">
                Fundraising Expertise/Skills <span className="text-gray-400">(optional)</span>
              </label>
              <p className="text-sm text-gray-500 mb-2">
                Any special fundraising experience or skills that you have
              </p>
              <textarea
                id="fundraisingExpertise"
                value={fundraisingExpertise}
                onChange={(e) => setFundraisingExpertise(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sla-blue focus:border-transparent"
              />
            </div>
          </div>

          {/* Ambassador Agreement */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b">
              Ambassador Agreement
            </h2>

            {/* Mission */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-sla-blue mb-2">Our Mission</h3>
              <p className="text-gray-700 text-sm">
                At School Lunch Angels, our mission is to lift the burden of school lunch debt from families
                in Harris County and work hand-in-hand with our schools to make school lunches something
                every child can look forward to. By ensuring that every student has access to a nourishing,
                worry-free meal, we help build a stronger, healthier future for our entire community.
              </p>
            </div>

            {/* Agreement */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-gray-800 mb-2">Ambassador Agreement</h3>
              <p className="text-gray-700 text-sm mb-3">
                As a School Lunch Angels Ambassador, I agree to represent the organization in a positive
                and professional manner. I understand that Ambassadors are community champions who help
                raise awareness and support for eliminating school lunch debt. I will:
              </p>
              <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 ml-2">
                <li>Share the mission of School Lunch Angels truthfully and respectfully.</li>
                <li>Conduct myself in a way that reflects positively on the organization.</li>
                <li>Support fundraising efforts and community outreach initiatives.</li>
                <li>Respect the privacy and dignity of the families and students we serve.</li>
              </ul>
            </div>

            {/* Acknowledgement Checkbox */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-3">Acknowledgement</h3>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreementAccepted}
                  onChange={(e) => setAgreementAccepted(e.target.checked)}
                  className="w-5 h-5 mt-0.5 text-sla-blue rounded focus:ring-sla-blue"
                />
                <span className="text-gray-700">
                  I affirm my commitment to the mission of School Lunch Angels and my agreement to the
                  responsibilities of an Ambassador. <span className="text-red-500">*</span>
                </span>
              </label>
            </div>
          </div>

          {/* Referral Source */}
          <div className="mb-8">
            <ReferralSourceField
              value={referralSource}
              otherValue={referralSourceOther}
              onChange={setReferralSource}
              onOtherChange={setReferralSourceOther}
              error={!!error && !referralSource}
            />
          </div>

          {/* Annual Membership Notice */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            Ambassador membership is valid for <strong>one year</strong> from the date of registration.
            To continue as an ambassador, membership must be renewed annually.
          </div>

          {/* Submit Button */}
          <div className="border-t pt-6">
            <button
              type="submit"
              disabled={submitting || !agreementAccepted}
              className="w-full bg-sla-blue hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <LoadingSpinner />
                  Processing...
                </>
              ) : (
                <>
                  I agree and continue to payment - {formatCurrency(AMBASSADOR_PRICE_CENTS)}
                </>
              )}
            </button>
            <p className="text-center text-sm text-gray-500 mt-3">
              You will be redirected to Stripe to complete your payment securely.
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>
            Questions? Contact us at{' '}
            <a href="mailto:secretary@schoollunchangels.org" className="text-sla-blue hover:underline">
              secretary@schoollunchangels.org
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
